import games from '../../data/games.json';
import type { AppDef, AppContext } from '../../types/window';
import type { GameEntry } from '../../types/content';
import { el, button } from './dom';

const catalog = games as GameEntry[];
const syncers = new WeakMap<HTMLElement, (ctx: AppContext) => void>();

export function resolvePlayable(gameId: unknown): GameEntry | undefined {
  if (typeof gameId !== 'string' || !gameId) return undefined;
  const g = catalog.find((x) => x.id === gameId);
  if (!g || g.status !== 'playable' || g.launch.mode !== 'iframe') return undefined;
  return g;
}

function mountIframe(g: GameEntry): HTMLIFrameElement {
  const launch = g.launch;
  if (launch.mode !== 'iframe') {
    throw new Error('expected iframe launch');
  }
  const frame = el('iframe', {
    attrs: {
      title: g.title,
      'aria-label': `${g.title} game`,
      src: launch.src,
      allow: 'autoplay; fullscreen',
      allowfullscreen: '',
    },
  });
  if (launch.sandbox) frame.setAttribute('sandbox', launch.sandbox);
  return frame;
}

function renderList(root: HTMLElement, ctx: AppContext): void {
  ctx.setTitle('Games');
  const wrap = el('div', { className: 'app-stack' });
  wrap.append(el('h1', { text: 'Games' }), el('p', { text: 'Player 1. More cabinets later.' }));
  for (const g of catalog) {
    const row = el('div', { className: 'app-row' });
    const text = el('div');
    text.append(el('strong', { text: g.title }), el('div', { text: g.summary }));
    const actions = el('div');
    actions.append(
      el('span', {
        className: 'chip',
        text: g.status === 'playable' ? 'Playable' : 'Coming soon',
      }),
    );
    if (g.status === 'playable' && g.launch.mode === 'iframe') {
      actions.append(button('Play', () => ctx.openApp('games', { gameId: g.id })));
    }
    row.append(text, actions);
    wrap.append(row);
  }
  root.replaceChildren(wrap);
}

function renderPlay(root: HTMLElement, ctx: AppContext, g: GameEntry, onExit: () => void): void {
  ctx.setTitle(g.title);
  const stage = el('div', { className: 'game-stage' });
  const bar = el('div', { className: 'game-toolbar' });
  const hint = el('p', {
    text: g.controls ?? 'Click the game to play. Exit game to leave.',
  });
  hint.setAttribute('aria-live', 'polite');
  bar.append(button('Exit game', onExit), hint);
  const frame = mountIframe(g);
  stage.append(bar, frame);
  root.replaceChildren(stage);
  frame.addEventListener('pointerdown', () => frame.focus());
  queueMicrotask(() => frame.focus());
}

export const gamesApp: AppDef = {
  appId: 'games',
  title: 'Games',
  icon: 'games',
  defaultSize: { w: 760, h: 560 },
  minSize: { w: 420, h: 300 },
  singleton: true,
  mount(root, ctx) {
    let playingId: string | null = null;

    const exitGame = (): void => {
      playingId = null;
      renderList(root, ctx);
      ctx.openApp('games', { gameId: '' });
    };

    const sync = (nextCtx: AppContext): void => {
      const next = resolvePlayable(nextCtx.payload?.gameId);
      if (next && next.id === playingId) return;
      if (next) {
        playingId = next.id;
        renderPlay(root, nextCtx, next, exitGame);
        return;
      }
      if (playingId) return;
      renderList(root, nextCtx);
    };

    syncers.set(root, sync);
    sync(ctx);
    return () => {
      playingId = null;
      syncers.delete(root);
      root.replaceChildren();
    };
  },
  update(root, ctx) {
    syncers.get(root)?.(ctx);
  },
};
